import { ChangeDetectionStrategy, Component, input, output, signal, WritableSignal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  AllEmailActions,
  EmailAdminFilterKey,
  EmailStatus,
  EmailTag,
  ZoudashUtils,
} from '@workspace/data-types';
import {
  AbstractSignalFormComponent,
  DataHeaderFilter,
  FilterChangedDetection,
  FilterRecord,
} from '@workspace/ng-aura';
import {
  EmailActionDisplayPipe,
  EmailStatusDisplayPipe,
  EmailTagDisplayPipe,
} from '@workspace/ng-data-access';
import { EmailColumnKey } from '../abstract-emails-data-table.component';

export interface EmailFilterModel {
  action: string | string[] | null;
  tag: string | string[] | null;
  status: string | string[] | null;
}

@Component({
  selector: 'core-emails-data-table-filter',
  templateUrl: './emails-data-table-filter.component.html',
  imports: [
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatSlideToggleModule,
    EmailActionDisplayPipe,
    EmailTagDisplayPipe,
    EmailStatusDisplayPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailsDataTableFilterCoreComponent extends AbstractSignalFormComponent<
  Partial<Record<EmailAdminFilterKey, string | string[]>>,
  EmailFilterModel
> {
  filterChanged = output<FilterChangedDetection>();
  currentFilterView = input<string | null>(null);
  filters = input.required<DataHeaderFilter<EmailColumnKey>[]>();
  emailActionValues = Object.values(AllEmailActions);
  emailTagValues = Object.values(EmailTag);
  emailStatusValues = Object.values(EmailStatus);
  emailAdminFilterKey = EmailAdminFilterKey;

  override model: WritableSignal<EmailFilterModel> = signal<EmailFilterModel>({
    action: null,
    tag: null,
    status: null,
  });

  override formTree = form(this.model);

  override emitDto(newDto: Partial<Record<EmailAdminFilterKey, string | string[]>>): void {
    this.filterChanged.emit({
      filter: newDto as FilterRecord,
      changed: !ZoudashUtils.isEqual(newDto, this.ogValue),
    });
  }

  protected override buildModelFromDto(
    dto: Partial<Record<EmailAdminFilterKey, string | string[]>>,
  ): EmailFilterModel {
    return {
      action: dto?.[EmailAdminFilterKey.Action] ?? null,
      tag: dto?.[EmailAdminFilterKey.Tag] ?? null,
      status: dto?.[EmailAdminFilterKey.Status] ?? null,
    };
  }

  protected override buildDtoFromModel(
    model: EmailFilterModel,
  ): Partial<Record<EmailAdminFilterKey, string | string[]>> {
    const dto: Partial<Record<EmailAdminFilterKey, string | string[]>> = {
      action: model.action ?? undefined,
      tag: model.tag ?? undefined,
      status: model.status ?? undefined,
    };
    return Object.fromEntries(Object.entries(dto).filter(([_, v]) => v != null)) as Partial<
      Record<EmailAdminFilterKey, string | string[]>
    >;
  }
}
